import {
  outer,
  help,
  messages,
  message,
  fieldset,
  $for,
  $attrs,
  createSection,
} from '@formkit/inputs'
import { computed } from 'vue'
import { v4 } from 'uuid'
import { FormKitNodeType } from '@formkit/core'

// This is a custom FormKit component definition. See the links below (in roughy
// that order) for reference.
// https://formkit.com/essentials/custom-inputs
// https://formkit.com/guides/export-and-restructure-inputs
// https://formkit.com/essentials/schema
// https://github.com/formkit/formkit/blob/master/packages/inputs/src/createSection.ts
// https://formkit.com/api-reference/formkit-inputs
// https://formkit.com/essentials/architecture
//
// We're using a schema, with custom definition, without createInput(), without
// <FormKitSchema :schema />, because this is the only way to create our own
// custom markup without the default FormKit structure getting in the way.
//
// The schema definitions are surprisingly flexible and I even found a nice
// method for writing custom styles by adding a data attribute to the wrapper
// then creating a <style> section. Also don't be afraid to read the source code
// if you need to.

// Constructs an expression that will output custom classes along with formkit
// section classes (i.e. :wrapper-class="" or :table-class="").
const addFormkitClasses = (classesString, classesProp) => `$:'${classesString} ' + $classes.${classesProp}`

// Our custom feature that adds the functionality
const tableOptionsFeature = node => {
  node.on('created', () => {
    // Anything defined outside of the feature will be shared by all instances
    const uuid = v4()

    // Internal values store
    const valuesStore: Array<string> = []

    const getOptionId = value => `table-options_${value}_${uuid}`
    const allId = `table-all-${uuid}`

    const indexByValue = computed(() => node.props.options.reduce((map, { value }, index) => {
      map[value] = index
      return map
    }, {}))

    // Slightly less simple than just accessing them by index and depending on
    // indexes always being in the right order, but a lot more sure to get
    // values in the same order every time.
    const setOptionValue = (value, checked) => {
      // Store values at a defined index so the list always has the same order
      // as the prop definintion
      valuesStore[indexByValue.value[value]] = checked ? value : undefined
    }

    // Commit the value to our node
    const inputNodeValue = () => {
      // Filter undefined from removed values
      node.input(valuesStore.filter(value => value))
    }

    // Hande input from one of the options
    const handleOption = event => {
      const { checked, value } = event.target

      setOptionValue(value, checked)

      const allCheckbox = document.getElementById(allId) as HTMLInputElement
      const allOptionsChecked = valuesStore.filter(value => value).length === node.props.options.length

      if (allOptionsChecked !== allCheckbox?.checked) {
        // Don't trigger the input handler for the element
        allCheckbox.checked = allOptionsChecked
      }

      inputNodeValue()
    }

    // Handle input from the overall option
    const handleAll = event => {
      const allChecked = event.target.checked

      for (let index = 0; index < node.props.options.length; index++) {
        const option = node.props.options[index]
        const checkbox = document.getElementById(getOptionId(option.value)) as HTMLInputElement
        // Don't do work on inputs that already match our target
        if (checkbox?.checked !== allChecked) {
          // Don't trigger the input handler for the element
          checkbox.checked = allChecked
        }

        setOptionValue(option.value, allChecked)
      }

      inputNodeValue()
    }

    // Expose data and handlers to the schema context
    Object.assign(node.context, {
      allId,
      getOptionId,
    })

    Object.assign(node.context.handlers, {
      handleOption,
      handleAll,
    })
  })
}

// Define our own schema definitions
const table = createSection('table', () => ({
  $el: 'table',
  attrs: {
    class: addFormkitClasses('w-100', 'table'),
    style: {
      borderSpacing: '10px',
    },
  },
}))

const header = createSection('header', () => ({
  $el: 'thead',
  children: [
    {
      $el: 'tr',
      attrs: {
        class: addFormkitClasses('bg-off-white text-uppercase font-weight-bold', 'header'),
      },
      children: [
        {
          $el: 'th',
          attrs: {
            class: 'pl-3 py-2',
          },
          children: [
            // This div is necessary because display: flex will mess up tables
            {
              $el: 'div',
              attrs: {
                class: 'd-flex align-items-center',
              },
              children: [
                {
                  $el: 'input',
                  attrs: {
                    id: '$allId',
                    type: 'checkbox',
                    value: 'all',
                    class: addFormkitClasses('', 'allCheckbox'),
                    // Handle input
                    onInput: '$handlers.handleAll',
                  },
                },
              ],
            },
          ],
        },
        {
          $el: 'th',
          children: [
            {
              $el: 'label',
              attrs: {
                for: '$allId',
                class: 'mb-0 font-weight-bold',
              },
              children: [
                '$label',
              ],
            },
          ],
        },
        // Output header meta to create columns
        {
          $el: 'th',
          for: ['item', '$meta'],
          attrs: {
            class: 'pr-3 text-right',
          },
          children: [
            '$item',
          ],
        },
      ],
    },
  ],
}))

// $option has to be in scope for this
const row = createSection('row', () => ({
  $el: 'tr',
  attrs: {
    class: addFormkitClasses('border-bottom', 'row'),
  },
  children: [
    {
      $el: 'td',
      attrs: {
        class: 'pl-3 py-1',
      },
      children: [
        // This div is necessary because display: flex will mess up tables
        {
          $el: 'div',
          attrs: {
            class: 'd-flex align-items-center',
          },
          children: [
            {
              $el: 'input',
              attrs: {
                id: '$getOptionId($option.value)',
                type: 'checkbox',
                value: '$option.value',
                class: addFormkitClasses('', 'checkbox'),
                onInput: '$handlers.handleOption',
              },
            },
          ],
        },
      ],
    },
    {
      $el: 'td',
      children: [
        {
          $el: 'label',
          attrs: {
            for: '$getOptionId($option.value)',
            class: 'mb-0 font-weight-bold',
          },
          children: [
            '$option.label',
          ],
        },
      ],
    },
    // Output option meta
    {
      $el: 'td',
      for: ['meta', '$option.meta'],
      attrs: {
        class: 'pr-3 text-right',
      },
      children: [
        '$meta',
      ],
    },
  ],
}))

// In case it's needed later:
// const styleScope = v4()
// const styles = createSection('styles', () => ({
//   $el: 'style',
//   attrs: {
//     type: 'text/css',
//   },
//   children: [
//     `
//     [data-style-scope="${styleScope}"] > .formkit-messages.form-row {
//       margin: 0;
//     }
//     [data-style-scope="${styleScope}"] > .formkit-messages.form-row > .formkit-message {
//       margin: 0;
//       padding: 0;
//     }
//     `,
//   ],
// }))

// $attrs({ 'data-style-scope': styleScope }, outer(
//   // ...
// ))

const schema = $attrs({ style: { overflowX: 'auto' } }, outer(
  $attrs(
    {
      class: addFormkitClasses('table-wrapper m-0 p-0 border-0', 'fieldset'),
      id: undefined,
    },
    fieldset(

      table(
        header(),
        $for('option', '$options',
          row(),
        ),
      ),
      help('$help'),

    ),
  ),

  $attrs(
    { style: { maxWidth: '100%', marginLeft: 0, marginRight: 0 } },
    messages(
      $attrs(
        { style: { marginLeft: 0, marginRight: 0, paddingLeft: 0, paddingRight: 0 } },
        message('$message.value'),
      ),
    ),
  ),

))

export const gsgTableOptions = {
  schema,
  type: 'table-options' as FormKitNodeType,
  props: ['options', 'meta'],
  features: [tableOptionsFeature],
  schemaMemoKey: 'table-options',
}
